import { signal, computed, effect } from '@preact/signals';
import { sfx } from './game/audio';
import type { LobbyState, MatchRewards, MatchStart, PlayerOutcome, Profile, RoleInfo, SelfInfo } from '@cratered/shared';
import type { DiscordBridge, LayoutMode, Participant } from './discord';
import type { Net, NetStatus } from './net';

export type Screen = 'lobby' | 'locker' | 'campaign' | 'match' | 'results';

export const discord = signal<DiscordBridge | null>(null);
export const net = signal<Net | null>(null);
export const netStatus = signal<NetStatus>('connecting');
export const self = signal<SelfInfo | null>(null);
export const profile = signal<Profile | null>(null);
export const lobby = signal<LobbyState | null>(null);
export const participants = signal<Participant[]>([]);
export const speaking = signal<Set<string>>(new Set());
export const layoutMode = signal<LayoutMode>('focused');
export const screen = signal<Screen>('lobby');
export const match = signal<MatchStart | null>(null);
export const results = signal<{ rewards: Record<string, MatchRewards>; outcomes: PlayerOutcome[] } | null>(null);
/** The server handed this client the running simulation (host left mid-match); the match session picks it up. */
export const hostGrant = signal<MatchStart | null>(null);
/** This page joined (or reloaded into) a match already in progress, so it owes itself a full sync from the host. */
export const joinedInProgress = signal(false);
export const toast = signal<{ text: string; kind: 'info' | 'error'; id: number } | null>(null);
export const helpOpen = signal(false);

const SOUND_KEY = 'cratered.sound';
function loadSound(): boolean {
  try {
    return localStorage.getItem(SOUND_KEY) !== 'off';
  } catch {
    return true;
  }
}
/** Sound effects on or off; remembered per browser. */
export const sound = signal(loadSound());
effect(() => {
  sfx.setMuted(!sound.value);
  try {
    localStorage.setItem(SOUND_KEY, sound.value ? 'on' : 'off');
  } catch {
    /* private mode: just this session */
  }
});
/** What this player may do (admin: hand out roles; creator: upload custom art) and who the creators are. */
export const roles = signal<RoleInfo & { creators: { id: string; name?: string }[] }>({ admin: false, creator: false, creators: [] });

export const isHost = computed(() => !!self.value && lobby.value?.hostId === self.value.id);
export const me = computed(() => lobby.value?.players.find((p) => p.id === self.value?.id) ?? null);

let toastId = 0;
let toastTimer: ReturnType<typeof setTimeout> | null = null;
export function showToast(text: string, kind: 'info' | 'error' = 'info'): void {
  toast.value = { text, kind, id: ++toastId };
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => (toast.value = null), kind === 'error' ? 5000 : 3000);
}
